import { useMemo } from "react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Heading,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import type { APIListing } from "models/types/api-listing";
import PlayIcon from "assets/icons/medium/play.svg?react";
import { AgencyLogo } from "components/agency-logo";
import { GradientMaskedImage } from "components/gradient-masked-image";
import type { Stat } from "components/stats-grid";
import { useAuth } from "contexts/auth-provider";
import { useIsAgencyFeaturePreferenceEnabled } from "hooks/use-is-agency-feature-preference-enabled";
import { sortFeedbackMostRecentFirst } from "modules/vendor-portal/screens/feedback/utils";
import { AgentFeedbackSection } from "modules/vendor-portal/screens/overview/components/welcome-presentation/agent-feedback-section";
import { FeedbackCard } from "modules/vendor-portal/screens/overview/components/welcome-presentation/feedback-card";
import { PresentationStat } from "modules/vendor-portal/screens/overview/components/welcome-presentation/presentation-stat";
import { RowSection } from "modules/vendor-portal/screens/overview/components/welcome-presentation/row-section";
import {
  hasMarketing,
  hasMarketingImages,
} from "modules/vendor-portal/utils/locked-routes-config";
import { getAggregatedPortalCampaignStat } from "modules/vendor-portal/utils/performance";
import { getHighestResolutionImageSource } from "utils/images";

type WelcomePresentationModalProps = {
  listing?: APIListing;
  hasBackgroundImage?: boolean;
};

export function WelcomePresentationModal({
  listing,
  hasBackgroundImage,
}: WelcomePresentationModalProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user, isVendor } = useAuth();
  const isAgencyFeaturePreferenceEnabled =
    useIsAgencyFeaturePreferenceEnabled();
  const showInterestLevel = isAgencyFeaturePreferenceEnabled(
    "vendor_portal.functionality.interest_level.show_interest_level_data",
  );
  const name = isVendor ? user?.contact_name : user?.given_name;
  const title = `Welcome back, ${name}`;

  const lastAgentUpdate = useMemo(
    () =>
      sortFeedbackMostRecentFirst(listing?.agent_updates?.entries || [])?.[0] ??
      null,
    [listing?.agent_updates],
  );

  const whoVisitedStats = useMemo(() => {
    return [
      {
        id: "total_attendance_count",
        value:
          listing?.summary
            ?.count_of_people_who_have_inspected_including_duplicates?.value,
        label: "Total attendance count",
      },
      {
        id: "total_attendees",
        value:
          listing?.summary?.count_of_unique_people_who_have_inspected?.value,
        label: "Total attendees",
      },
      {
        id: "total_repeat_attendees",
        value:
          listing?.summary
            ?.count_of_unique_people_who_have_inspected_more_than_once?.value,
        label: "Total repeat attendees",
      },
      // Interest-level tiles are gated behind the vendor-portal preference so
      // they don't leak buyer sentiment when the toggle is off. See CRM-14119.
      ...(showInterestLevel
        ? [
            {
              id: "hot_attendees",
              value:
                listing?.summary?.count_of_interested_groups_by_interest_level?.value?.find(
                  (stat) => stat.id === "hot",
                )?.value,
              label: "Hot attendees",
            },
            {
              id: "warm_attendees",
              value:
                listing?.summary?.count_of_interested_groups_by_interest_level?.value?.find(
                  (stat) => stat.id === "warm",
                )?.value,
              label: "Warm attendees",
            },
          ]
        : []),
    ].filter((s) => Number(s.value) > 0) as Stat[];
  }, [listing?.summary, showInterestLevel]);

  // Iterate over all ad campaigns, and sum the total clicks for each platform
  const totalClicksAcrossAllAdCampaigns = useMemo(
    () => getAggregatedPortalCampaignStat(listing, "clicks_key"),
    [listing],
  );

  const sortedApplicantFeedbackWithNotes = useMemo(() => {
    const entries = [...(listing?.applicant_feedback?.entries || [])];
    // When interest level is hidden, don't order by buyer sentiment — fall back
    // to most-recent-first so no interest-level data influences the view.
    const sorted = showInterestLevel
      ? entries.sort((a, b) => {
          const order = {
            hot: 2,
            warm: 1,
            cold: 0,
          };

          return (
            order[a.interest_level?.id as keyof typeof order] -
            order[b.interest_level?.id as keyof typeof order]
          );
        })
      : sortFeedbackMostRecentFirst(entries);

    return sorted.filter((feedback) => !!feedback.note);
  }, [listing?.applicant_feedback?.entries, showInterestLevel]);

  const showButton =
    sortedApplicantFeedbackWithNotes.length > 0 &&
    whoVisitedStats.length > 0 &&
    hasMarketing(listing) &&
    hasMarketingImages(listing);

  return (
    <>
      {showButton && (
        <Button
          onClick={() => onOpen()}
          rightIcon={<Icon as={PlayIcon} />}
          colorScheme={"white"}
          color={hasBackgroundImage ? undefined : "client.accent.text"}
          variant={"outline"}
          size={"xs"}
          flexShrink={0}
        >
          Play presentation
        </Button>
      )}
      <Modal isOpen={isOpen} onClose={onClose} isCentered={false} size={"full"}>
        <ModalContent
          pos={"relative"}
          h={"100%"}
          borderRadius={"none"}
          overflowY={"scroll"}
          overflowX={"hidden"}
        >
          <ModalHeader p={0}>
            <Box
              pos={"relative"}
              h={"100vh"}
              w={"100vw"}
              bg={!listing?.marketing?.images ? "gray.50" : undefined}
            >
              <GradientMaskedImage
                imageUrl={
                  listing?.marketing?.images.length
                    ? getHighestResolutionImageSource(
                        listing?.marketing?.images[0],
                      )?.url
                    : undefined
                }
                style={{
                  borderRadius: "none",
                  height: "100vh",
                }}
                bgGradient={
                  listing?.marketing?.images.length ? true : undefined
                }
                gradientStyle={{ height: "100%" }}
              >
                <VStack
                  h={"100%"}
                  py={{ base: "40px", md: "100px" }}
                  px={{ base: "10px", md: "100px" }}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <AgencyLogo imageProps={{ w: "min-content", h: 8 }} />
                  <VStack gap={{ base: 4, md: 8, lg: 12 }} color={"white"}>
                    <Heading
                      fontSize={{ base: "6xl", md: "56px" }}
                      fontWeight={{ base: 700 }}
                      lineHeight={{ base: "6xl", md: "56px" }}
                    >
                      {title}
                    </Heading>
                    <Text
                      fontSize={{ base: "3xl", md: "5xl" }}
                      fontWeight={400}
                    >
                      Here&apos;s what&apos;s been happening throughout the
                      campaign
                    </Text>
                  </VStack>
                  <VStack color={"white"}>
                    <Text fontSize={"14px"} fontWeight={400}>
                      Scroll to discover
                    </Text>
                    <ChevronDownIcon w={8} h={8} />
                  </VStack>
                </VStack>
              </GradientMaskedImage>
            </Box>
          </ModalHeader>
          {/* You must have the ModalCloseButton to ensure the modal opens at the
        correct scroll position. The z-index/opacity here is a hack to hide the close button.
        Display and visibility won't work. Chakra doesn't like us doing that. */}
          <ModalCloseButton zIndex={"-999"} opacity={0} />
          <ModalBody pos={"relative"} h={"auto"} p={0}>
            <Box
              w={"100%"}
              pos={"sticky"}
              top={"0"}
              zIndex={"sticky"}
              p={6}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              bg={"gray.900"}
            >
              <AgencyLogo imageProps={{ w: "auto", h: 8 }} />
              <Button
                variant={"solid"}
                bg={"gray.100"}
                color={"gray.700"}
                _hover={{ bg: "gray.200" }}
                onClick={onClose}
              >
                Exit
              </Button>
            </Box>

            <Box h={"auto"} w={"100%"}>
              {lastAgentUpdate ? (
                <VStack
                  minH={"100vh"}
                  gap={14}
                  p={{ base: 10, sm: 16, md: 20 }}
                  justify={"center"}
                >
                  <AgentFeedbackSection
                    date={lastAgentUpdate.date_of}
                    profileImage={
                      getHighestResolutionImageSource(
                        lastAgentUpdate.agent?.profile_image,
                      )?.url ?? ""
                    }
                    bodyText={lastAgentUpdate.note ?? ""}
                    agentName={lastAgentUpdate.agent?.name ?? ""}
                  />
                </VStack>
              ) : null}

              {(listing?.marketing?.images.length || 0) > 1 && (
                <Box minH={"100vh"}>
                  <Image
                    src={
                      getHighestResolutionImageSource(
                        listing?.marketing?.images[1],
                      )?.url
                    }
                    alt={"Secondary listing image"}
                    style={{
                      height: "100vh",
                      objectFit: "cover",
                      width: "100%",
                    }}
                  />
                </Box>
              )}

              {whoVisitedStats.length > 0 ? (
                <VStack
                  minH={"100vh"}
                  p={{ base: 10, sm: 16, md: 20 }}
                  justify={"center"}
                  align={"center"}
                >
                  <RowSection
                    heading={"Who visited your home throughout the campaign?"}
                  >
                    {whoVisitedStats.map((stat) => (
                      <PresentationStat
                        key={stat.id}
                        stat={stat}
                        numberOfStats={whoVisitedStats.length}
                      />
                    ))}
                  </RowSection>
                </VStack>
              ) : null}

              {(listing?.marketing?.images.length || 0) > 2 && (
                <Box minH={"100vh"}>
                  <Image
                    src={
                      getHighestResolutionImageSource(
                        listing?.marketing?.images[2],
                      )?.url
                    }
                    alt={"Tertiary listing image"}
                    style={{
                      height: "100vh",
                      objectFit: "cover",
                      width: "100%",
                    }}
                  />
                </Box>
              )}

              {sortedApplicantFeedbackWithNotes &&
                sortedApplicantFeedbackWithNotes.length > 0 && (
                  <VStack
                    minH={"100vh"}
                    p={{ base: 10, sm: 16, md: 20 }}
                    justify={"center"}
                    align={"center"}
                  >
                    <RowSection heading={"What's been happening?"}>
                      {sortedApplicantFeedbackWithNotes
                        .map((feedback, index) => (
                          <FeedbackCard
                            key={index}
                            feedbackNote={feedback.note}
                            attendeeName={feedback.group.name}
                            dateOfAttendance={feedback.date_of}
                            count={sortedApplicantFeedbackWithNotes.length}
                          />
                        ))
                        .filter(Boolean)}
                    </RowSection>
                  </VStack>
                )}

              <VStack
                pos={"relative"}
                minH={"100vh"}
                pt={{ base: 10, sm: 16, md: 20 }}
                pb={0}
                px={{ base: 10, sm: 16, md: 20 }}
                justify={"center"}
                align={"center"}
                bg={
                  (listing?.marketing?.images.length || 0) < 1
                    ? "gray.50"
                    : undefined
                }
              >
                <RowSection
                  heading={
                    <>
                      Your listing was clicked{" "}
                      <strong>{totalClicksAcrossAllAdCampaigns} times</strong>{" "}
                      online throughout the campaign
                    </>
                  }
                >
                  {null}
                </RowSection>
                <ModalFooter
                  pos={"absolute"}
                  bottom={"6"}
                  border={"none"}
                  justifyContent={"center"}
                  p={{ base: 0, sm: 6 }}
                >
                  <Button
                    variant={"solid"}
                    bg={"gray.100"}
                    color={"gray.700"}
                    _hover={{ bg: "gray.200" }}
                    size={"lg"}
                    onClick={onClose}
                  >
                    Go to Overview
                  </Button>
                </ModalFooter>
              </VStack>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
